import { DASHBOARD_MESSAGES } from "@/modules/Dashboards/AdminDashboard/constants/AdminDashboardConstants";
import EmptyState from "@/shared/components/EmptyState/EmptyState.vue";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import { PropertiesChart } from "../../models/PropertiesChart";
import { SectionOfDashboard } from "../../models/SectionOfDashboard";
import ChartTypeMenu from "../ChartTypeMenu/ChartTypeMenu.vue";
import { getDynamicItemSize } from "../DraggableWrapper/utils/getDynamicItemSize";
import BarChart from "./BarChart/BarChart.vue";
import DoughnutChart from "./DoughnutChart/DoughnutChart.vue";
import LineChart from "./LineChart/LineChart.vue";
import PieChart from "./PieChart/PieChart.vue";
import {
  default as StackedBar,
  default as StackedBarChart
} from "./StackedBarChart/StackedBarChart.vue";
import StackedLineChart from "./StackedLineChart/StackedLineChart.vue";
import StackedPolarChart from "./StackedPolarChart/StackedPolarChart.vue";
import StackedRadialChart from "./StackedRadialChart/StackedRadialChart.vue";
import { getCollectionFilter } from "./Utils/getCollectionFilter";
import { handleClickChart } from "./Utils/handleClick";
import { CollectionFilter } from "../../models/CollectionFilter";

declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}
type ResizeObserverCallback = (
  entries: ResizeObserverEntry[],
  observer: ResizeObserver
) => void;
interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
}

@Component({
  name: "Charts",
  components: {
    EmptyState,
    ChartTypeMenu,
    StackedBar,
    BarChart,
    StackedBarChart,
    StackedPolarChart,
    StackedRadialChart,
    PieChart,
    DoughnutChart,
    StackedLineChart,
    LineChart
  },
  computed: {
    ...mapGetters({
      sectionsOfDashboardStore: "getSectionsOfdashboard",
      refreshCharts: "getRefreshCharts"
    })
  }
})
export default class Charts extends Vue {
  @Prop() propertiesToChart!: PropertiesChart;

  @Prop({ default: false }) autoResize!: boolean;
  @Prop({ default: false }) onFilter!: boolean;
  @Prop({ default: false }) disableClick!: boolean;
  @Prop({ default: false }) renderFromDashboardTemplate!: boolean;

  dataToGraph: CollectionFilter | null = null;

  sectionsOfDashboardStore!: Array<SectionOfDashboard>;
  refreshCharts!: number;
  EMPTY_STATE_HEADER = DASHBOARD_MESSAGES.NO_RESULTS;
  EMPTY_STATE_PARAGRAPH = DASHBOARD_MESSAGES.NOT_FOUND;

  emptyData = true;

  width = 500;
  height = 400;
  isResizing = false;
  startX = 0;
  startY = 0;
  startWidth = 0;
  startHeight = 0;
  resizeObserver?: ResizeObserver;

  private chartMap: Record<string, any> = {
    bar: BarChart,
    "horizontal-bar": BarChart,
    pie: PieChart,
    doughnut: DoughnutChart,
    line: LineChart,
    stackedLine: StackedLineChart,
    stackedColumn: StackedBarChart,
    "horizontal-stackedColumn": StackedBarChart,
    stackedPolar: StackedPolarChart,
    stackedRadial: StackedRadialChart
  };
  roundBtnClass = STYLE_CLASSES.TERTIARY_ROUND_BTN_M0;

  openFilter() {
    this.$emit("openFilter");
  }
  chartTypeDefault = "";
  chartType = "";
  get internalPropertiesToChart() {
    return {
      ...this.propertiesToChart,
      chartType: this.chartType || this.propertiesToChart.chartType
    };
  }

  get chartComponent() {
    return this.chartMap[this.internalPropertiesToChart.chartType] || null;
  }
  beforeMount() {
    this.chartTypeDefault = this.propertiesToChart?.chartType || "";
  }
  mounted() {
    this.onChangeData();
    window.addEventListener("mouseleave", this.stopResize);

    if (this.autoResize) {
      this.$nextTick(() => {
        this.enableAutoResize();
        // Forzar múltiples intentos de cálculo para asegurar dimensiones correctas
        this.forceInitialResize();
      });
    }
  }

  private forceInitialResize() {
    const calculateSize = () => {
      const el = this.$refs.resizable as HTMLElement;
      if (!el || !el.parentElement) return;

      const { width, height } = getDynamicItemSize(el);
      if (width > 0 && height > 0) {
        this.width = width;
        this.height = height;
      }
    };

    // Primer intento inmediato
    requestAnimationFrame(() => {
      calculateSize();

      // Segundo intento después del primer frame
      requestAnimationFrame(() => {
        calculateSize();

        // Tercer intento con timeout como fallback
        setTimeout(() => {
          calculateSize();
        }, 100);
      });
    });
  }
  beforeDestroy() {
    window.removeEventListener("mouseleave", this.stopResize);

    this.disableAutoResize();
  }
  @Watch("autoResize")
  onAutoResizeChanged(val: boolean) {
    if (val) this.enableAutoResize();
    else this.disableAutoResize();
  }

  private enableAutoResize() {
    const el = this.$refs.resizable as HTMLElement;
    if (!el) return;

    // Asegurarse de limpiar si ya existe
    this.disableAutoResize();

    this.resizeObserver = new ResizeObserver(() => {
      const { width, height } = getDynamicItemSize(el);
      if (width <= 0 || height <= 0) return;

      this.width = width;
      this.height = height;
    });

    if (el.parentElement) {
      this.resizeObserver.observe(el.parentElement);
    }
  }

  private disableAutoResize() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  get boxStyle() {
    return {
      position: "relative",
      width: this.autoResize ? "100%" : this.width + "px",
      height: this.autoResize ? "100%" : this.height + "px",
      background: "#fff",
      border: "1px solid #aaa",
      borderRadius: "0.9rem",
      userSelect: "none"
    };
  }

  startResize(e: MouseEvent) {
    this.isResizing = true;

    // guardamos posición inicial
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startWidth = this.width;
    this.startHeight = this.height;

    window.addEventListener("mousemove", this.resize, { capture: true });
    window.addEventListener("mouseup", this.stopResize, { capture: true });
  }

  resize(e: MouseEvent) {
    if (!this.isResizing) return;

    // diferencia desde el inicio
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;

    // nuevo tamaño
    this.width = Math.max(100, this.startWidth + dx);
    this.height = Math.max(100, this.startHeight + dy);
  }

  stopResize() {
    this.isResizing = false;
    window.removeEventListener("mousemove", this.resize, true);
    window.removeEventListener("mouseup", this.stopResize, true);
  }

  onChartTypeChanged(newType: string) {
    if (newType != this.chartType) {
      this.chartType = newType;
      this.$emit("chartTypeChanged", this.chartType);
    }
  }

  onClick(event: any) {
    if (this.disableClick) return;
    handleClickChart(
      this.propertiesToChart.indexSectionOfDashboard,
      this.propertiesToChart.indexCollectionFilter,
      event.chartType,
      event.event
    );
  }

  @Watch("refreshCharts")
  @Watch("propertiesToChart", { deep: true })
  onChangeData() {
    this.chart();
    this.onChartTypeChanged(this.propertiesToChart.chartType);
  }
  chart() {
    const dataToGraph = getCollectionFilter(
      this.sectionsOfDashboardStore,
      this.propertiesToChart.indexSectionOfDashboard,
      this.propertiesToChart.indexCollectionFilter
    );
    if (!dataToGraph || !dataToGraph?.chartData?.data?.length) {
      this.emptyData = true;
    } else {
      this.dataToGraph = dataToGraph;
      this.emptyData = false;
    }
  }

  deleteChart() {
    this.$emit("deleteChart");
  }
}
